import { useContext, useEffect } from 'react';

import { useFormikContext } from 'formik';

import { Deal, DealStateEnum } from '../gql/dealGql';
import { Bank, DealType } from '../gql/generated/graphql';

import { OtherLienholder, OtherLienholderSlug } from '../constants/lienholders';
import { DealContext } from '../libs/DealContext';
import { findBankName, getDefaultBankByDealType } from '../utils/financialInfos';

import { useDefaultBankName } from './useBanks';

const OTHER_LENDER_NAME = 'Other';

type UseSetValuesByDealTypeProps = {
  banks?: Bank[];
};

export const useSetValuesByDealType = (props: UseSetValuesByDealTypeProps = {}) => {
  const { banks = [] } = props;

  const { deal, setUserChangedSomething } = useContext(DealContext);
  const { values, setFieldValue, setFieldTouched } = useFormikContext<Deal>();

  const isBeforeStructuring = [DealStateEnum.Estimate, DealStateEnum.SoftClose].includes(
    deal.state,
  );

  // https://github.com/formium/formik/issues/2059
  const updateField = (name: string, value: unknown) => {
    setFieldValue(name, value);
    setTimeout(() => setFieldTouched(name, true));
  };

  const defaultBank = useDefaultBankName(values.type);

  useEffect(() => {
    if (!isBeforeStructuring || !defaultBank) {
      return;
    }

    if (values.financial_info.bank !== defaultBank) {
      setUserChangedSomething(true);
      updateField('financial_info.bank', defaultBank);
    }
  }, [values.type, defaultBank]);

  useEffect(() => {
    if (isBeforeStructuring || banks.length === 0) {
      return;
    }

    const selectedBankIsAvailable = banks.some((bank) => bank.name === values.financial_info.bank);
    if (selectedBankIsAvailable) {
      return;
    }

    const availableDefaultBank = findBankName({
      banks,
      bankSlug: getDefaultBankByDealType(values.type),
    });

    setUserChangedSomething(true);
    updateField('financial_info.bank', availableDefaultBank ?? banks[0].name);
  }, [banks]);

  useEffect(() => {
    if (
      values.type === DealType.Refi &&
      (values.car.payoff.lienholder_slug !== OtherLienholderSlug ||
        values.car.payoff.lienholder_name !== OtherLienholder)
    ) {
      setUserChangedSomething(true);
      updateField('car.payoff.lienholder_slug', OtherLienholderSlug);
      updateField('car.payoff.lienholder_name', OtherLienholder);
    } else if (
      values.type !== DealType.Refi &&
      values.car.payoff.lender_name !== OTHER_LENDER_NAME
    ) {
      setUserChangedSomething(true);
      updateField('car.payoff.lender_name', OTHER_LENDER_NAME);
    }
  }, [values.type]);
};
